import React, { useContext, useEffect, useRef, useState } from 'react';
import { FirebaseContext } from 'context';
import {
  Button,
  EnterYourComment,
  Avatar,
  Comment as Reply,
  Dialog
} from 'components';
import styled, { css } from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage, isMultiLingual, defaultLanguage } from 'i18n';
import { Markup } from 'interweave';
import {
  PencilIcon,
  SpeechBubbleReply,
  TranslateIcon,
  TrashIcon
} from 'assets/svgs';
import {
  formatTimestamp,
  scrollToAnchor,
  clearQueryParams,
  isTranslated,
  rememberTranslated,
  forgetTranslated
} from 'utils';
import DeleteCommentDialog from './DeleteCommentDialog';

/* Note how down the end of <Container /> we have a <Reply /> component, which is just a renamed
<Comment /> component (see the import at the top of the file). This time, instead of passing in null
as it's parent, we pass in the current comment.id, so we're effectively recursively rendering <Comment />
components to create the comment tree.  */
const Comment = React.forwardRef(
  (
    {
      i,
      arr,
      parentCommentId,
      level,
      comment,
      comments,
      commentBeingEdited,
      idOfCommentBeingRepliedTo,
      commentIdQueryParam,
      setQuestionIdQueryParam,
      handleEditComment,
      postingOrEditingComment,
      setCommentBeingEdited,
      users,
      user,
      handlePostComment,
      setIdOfCommentBeingRepliedTo,
      handleDeleteComment,
      sessionId,
      deletingComment,
      handleDialog,
      onlineUsers,
      batchTranslating,
      onManualTranslate
    },
    ref
  ) => {
    const t = useTranslations('Review');
    const tCommon = useTranslations('Common');
    const { language } = useLanguage();
    const { firebase } = useContext(FirebaseContext);
    const userWantsToEditThisComment = commentBeingEdited?.id === comment.id;
    const userWantsToReplyToThisComment =
      idOfCommentBeingRepliedTo === comment.id;
    const timestamp = formatTimestamp(comment.timestamp, language);
    const [showDeleteDialog, setShowDeleteCommentDialog] = useState(false);

    // On-demand translation (§6.3): in-place swap with a "Show original"
    // toggle, offered when the comment's authored language differs from the
    // reader's. The choice persists per browser (see the restore effect).
    const [translation, setTranslation] = useState(null);
    const [showTranslation, setShowTranslation] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [translateFailed, setTranslateFailed] = useState(false);
    // Comments written before `language` was stored have none at all, so a
    // bare comparison against `undefined` never matched and offered Translate
    // on every comment — including English ones being read in English. The
    // instance was single-language when those were written, so its default is
    // the best available guess. This fails safe in the right direction: a
    // wrong guess only ever offers a translation that was not needed, it never
    // withholds one that was.
    const commentLanguage = comment.language ?? defaultLanguage;
    const canTranslate = isMultiLingual && commentLanguage !== language;

    // A language switch or an edit of the comment invalidates whatever was
    // fetched. Before falling back to the original, restore what the reader
    // wants for this comment — but ONLY from an already-warm cache. Fetching
    // here would turn a page load into a silent burst of claude-opus-5 calls
    // nobody asked for, which is the very thing §6.3 rejects eager
    // translation to avoid.
    //
    // Wanting a translation that is not cached yet is not an error and the
    // stored intent is deliberately kept: editing a comment clears its cache,
    // and the batch job fills caches after the fact, so re-running this effect
    // when translationCache changes is how those translations appear without
    // a reload. Entries for comments that never come back age out via the
    // store's FIFO cap rather than being pruned here.
    useEffect(() => {
      const cached = comment.translationCache?.[language];
      if (isTranslated(comment.id, language) && cached) {
        setTranslation(cached);
        setShowTranslation(true);
        return;
      }
      setTranslation(null);
      setShowTranslation(false);
    }, [language, comment.html, comment.id, comment.translationCache]);

    const handleTranslateClick = async () => {
      // Every branch records the choice, so it survives a refresh in both
      // directions — presence means translated, absence means original.
      if (showTranslation) {
        setShowTranslation(false);
        forgetTranslated(comment.id, language);
        return;
      }
      if (translation) {
        setShowTranslation(true);
        rememberTranslated(comment.id, language);
        return;
      }
      // The live subscription already streams translationCache — a warm
      // cache never needs the network.
      const cached = comment.translationCache?.[language];
      if (cached) {
        setTranslation(cached);
        setShowTranslation(true);
        rememberTranslated(comment.id, language);
        if (onManualTranslate) onManualTranslate();
        return;
      }
      // Recorded before the await, not after: the restore effect re-runs on
      // every snapshot, and without the entry already in place an in-flight
      // translation would be reset to the original mid-fetch.
      rememberTranslated(comment.id, language);
      // Counted whether or not a fetch was needed. The threshold reads the
      // reader's intent, not our costs — and counting only fetches made the
      // offer unreachable on a question whose comments another reader had
      // already warmed, which is exactly where it is most useful.
      if (onManualTranslate) onManualTranslate();
      setTranslating(true);
      try {
        const result = await firebase.questions.translateComment({
          sessionId,
          questionId: comment.questionId,
          commentId: comment.id,
          targetLanguage: language
        });
        setTranslation(result.data.html);
        setShowTranslation(true);
      } catch (error) {
        console.error('Comment translation failed:', error);
        setTranslateFailed(true);
        setTimeout(() => setTranslateFailed(false), 3000);
      } finally {
        setTranslating(false);
      }
    };

    // A batch is filling this question's missing translations and this comment
    // is one of the ones still waiting — dim it the same way a single-comment
    // translation does, so one operation does not have two visual languages.
    // Comments already showing a translation (warm cache) are left alone.
    const awaitingBatch = batchTranslating && canTranslate && !showTranslation;
    const pendingTranslation = translating || awaitingBatch;

    const hasReplies = comments.some((c) => c.parentCommentId === comment.id);
    const commentRef = useRef(null);
    const lastReplyRef = useRef(null);
    const replyRefs = useRef([]);
    const deleteCommentDialogRef = useRef(null);

    useEffect(() => {
      const scrollToThisComment = commentIdQueryParam
        ? arr.some((_comment) => _comment.id === commentIdQueryParam)
        : false;

      if (scrollToThisComment) {
        scrollToAnchor({
          anchor: commentIdQueryParam,
          offset: -64,
          duration: 100
        });
        setQuestionIdQueryParam(null);
        clearQueryParams();
      }
    }, []);

    const handleDismissDeleteCommentDialog = (e) => {
      e.stopPropagation();
      handleDialog({
        dialogRef: deleteCommentDialogRef,
        animation: 'dismiss',
        stateHandler: () => setShowDeleteCommentDialog(false)
      });
    };

    return (
      <Container
        ref={(el) => {
          commentRef.current = el;
          if (ref) {
            if (typeof ref === 'function') ref(el);
            else ref.current = el;
          }
        }}
        id={comment.id}
        key={comment.id}
        $userWantsToEditThisComment={userWantsToEditThisComment}
        $lastComment={i === arr.length - 1}
        $isReply={parentCommentId}
        $level={level}
        $idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
        $isBeingRepliedTo={idOfCommentBeingRepliedTo === comment.id}>
        <TopLine>
          <Avatar
            avatarUrl={comment.avatarUrl}
            alt={comment.name}
            showConnector={hasReplies}
            commentRef={commentRef}
            lastReplyRef={lastReplyRef}
            replyRefs={replyRefs}
            level={level}
            isOnline={onlineUsers?.some((u) => u.id === comment.uid)}
          />
          <h4>
            {comment.name}
            {users
              ?.find((u) => u.id === comment.uid)
              ?.isModerator?.includes(sessionId) && (
              <>
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <IsModerator>{t('moderator')}</IsModerator>
              </>
            )}
            {timestamp && (
              <>
                <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                <Timestamp>{timestamp}</Timestamp>
              </>
            )}
          </h4>
        </TopLine>
        <div style={{ display: userWantsToEditThisComment ? 'none' : 'block' }}>
          {/* Dimmed rather than replaced while translating: the comment is
              already readable, so swapping it for a spinner would remove
              information the reader has and — since translated de/fr run
              materially longer — reflow the thread twice instead of once.
              The Button carries the spinner and disables itself, so the
              action is already acknowledged at the point of the click.
              aria-live wraps only the body, not Buttons, so label changes
              ("Translate" → "Show original") are not announced as content. */}
          <TranslatableBody
            $translating={pendingTranslation}
            aria-busy={pendingTranslation}
            aria-live="polite">
            <Markup
              content={
                showTranslation && translation ? translation : comment.html
              }
              tagName="div"
              className="markup"
            />
          </TranslatableBody>
          {/* The visible failure signal is the button's red label, which
              self-clears after 3s; screen readers get it here instead. */}
          {translateFailed && (
            <VisuallyHidden role="alert">{t('translateError')}</VisuallyHidden>
          )}
          <Buttons>
            {comment.uid === user.uid && (
              <Button
                $variant="text"
                textColor="var(--blue)"
                icon={<PencilIcon width="16px" height="15px" />}
                onClick={() => {
                  if (userWantsToReplyToThisComment) {
                    setIdOfCommentBeingRepliedTo(null);
                  }
                  setCommentBeingEdited(comment);
                }}>
                {tCommon('edit')}
              </Button>
            )}
            <Button
              $variant="text"
              textColor="var(--blue)"
              icon={<SpeechBubbleReply width="17px" height="15px" />}
              onClick={() => {
                if (userWantsToEditThisComment) {
                  setCommentBeingEdited(null);
                }
                setIdOfCommentBeingRepliedTo(comment.id);
              }}>
              {t('reply')}
            </Button>
            {canTranslate && (
              <Button
                $variant="text"
                textColor={translateFailed ? 'var(--red)' : 'var(--blue)'}
                icon={<TranslateIcon width="15px" height="15px" />}
                loading={translating}
                onClick={handleTranslateClick}>
                {translateFailed
                  ? t('translateError')
                  : showTranslation
                    ? t('showOriginal')
                    : t('translate')}
              </Button>
            )}
            {user.isModerator?.includes(sessionId) && (
              <Button
                $variant="text"
                textColor="var(--blue)"
                icon={
                  <TrashIcon
                    width="15px"
                    style={{ position: 'relative', bottom: '1px' }}
                  />
                }
                onClick={() => setShowDeleteCommentDialog(true)}>
                {tCommon('delete')}
              </Button>
            )}
          </Buttons>
        </div>
        <div style={{ display: userWantsToEditThisComment ? 'block' : 'none' }}>
          <EnterYourComment
            key={comment.id}
            handleEditComment={handleEditComment}
            commentBeingEdited={commentBeingEdited}
            postingOrEditingComment={postingOrEditingComment}
            setCommentBeingEdited={setCommentBeingEdited}
            userWantsToEditThisComment={userWantsToEditThisComment}
            users={users}
            uid={user.uid}
            parentComment={comment}
          />
        </div>
        <div
          style={{ display: userWantsToReplyToThisComment ? 'block' : 'none' }}>
          <EnterYourComment
            key={comment.id}
            handlePostComment={handlePostComment}
            postingOrEditingComment={postingOrEditingComment}
            userWantsToReplyToThisComment={userWantsToReplyToThisComment}
            idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
            setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
            users={users}
            uid={user.uid}
            parentComment={comment}
          />
        </div>
        {comments
          .filter((_comment) => _comment.parentCommentId === comment.id)
          .map((_comment, _i, _arr) => (
            <Reply
              ref={(el) => {
                if (_i === _arr.length - 1) {
                  lastReplyRef.current = el;
                }
                replyRefs.current[_i] = el;
              }}
              i={_i}
              arr={_arr}
              commentBeingEdited={commentBeingEdited}
              comments={comments}
              comment={_comment}
              handleEditComment={handleEditComment}
              handlePostComment={handlePostComment}
              idOfCommentBeingRepliedTo={idOfCommentBeingRepliedTo}
              key={_comment.id}
              level={level + 1}
              parentCommentId={_comment.parentCommentId}
              postingOrEditingComment={postingOrEditingComment}
              setCommentBeingEdited={setCommentBeingEdited}
              setIdOfCommentBeingRepliedTo={setIdOfCommentBeingRepliedTo}
              setQuestionIdQueryParam={setQuestionIdQueryParam}
              user={user}
              users={users}
              sessionId={sessionId}
              commentIdQueryParam={commentIdQueryParam}
              handleDeleteComment={handleDeleteComment}
              deletingComment={deletingComment}
              handleDialog={handleDialog}
              onlineUsers={onlineUsers}
              batchTranslating={batchTranslating}
              onManualTranslate={onManualTranslate}
            />
          ))}
        {level === 0 && <Divider />}
        <Dialog
          ref={deleteCommentDialogRef}
          isVisible={showDeleteDialog}
          onDismiss={handleDismissDeleteCommentDialog}
          contentStyle={{ maxWidth: '454px' }}>
          <DeleteCommentDialog
            onConfirm={() => {
              if (userWantsToReplyToThisComment) {
                setIdOfCommentBeingRepliedTo(null);
              }
              if (userWantsToEditThisComment) {
                setCommentBeingEdited(null);
              }
              handleDeleteComment(comment);
              setShowDeleteCommentDialog(false);
            }}
            onDismiss={handleDismissDeleteCommentDialog}
            deleting={deletingComment}
          />
        </Dialog>
      </Container>
    );
  }
);

/**
 * Dims while a translation is in flight and fades back as the new text lands,
 * so the swap reads as a change rather than an abrupt substitution — easy to
 * miss otherwise when the two languages happen to be similar in length.
 * `prefers-reduced-motion` drops the transition but keeps the dim, since the
 * dim is the state signal and the fade is only decoration.
 */
const TranslatableBody = styled.div`
  opacity: ${({ $translating }) => ($translating ? 0.55 : 1)};
  transition: opacity 150ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Announced by screen readers, invisible to everyone else. Standard clip
// pattern rather than display:none, which would remove it from the a11y tree.
const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 0.063rem;
  left: -1.5rem;
  margin-top: 18px;
  position: relative;
  width: calc(100% + 2.25rem);

  @media (min-width: 768px) {
    margin-top: 10px;
    left: -3.9rem;
    width: calc(100% + 1.9rem);
  }
`;

const Container = styled.div`
  background-color: #fff;
  padding: ${({ $lastComment, $userWantsToEditThisComment, $isReply }) =>
    $lastComment && !$userWantsToEditThisComment && !$isReply
      ? '33px 22px 16px 35px'
      : $lastComment && $userWantsToEditThisComment && !$isReply
        ? '33px 22px 9px 35px'
        : $isReply
          ? '23px 0px 0px 23px'
          : '33px 22px 0px 35px'};

  @media (min-width: 768px) {
    padding: ${({ $lastComment, $userWantsToEditThisComment, $isReply }) =>
      $lastComment && !$userWantsToEditThisComment && !$isReply
        ? '33px 22px 16px 116px'
        : $lastComment && $userWantsToEditThisComment && !$isReply
          ? '33px 22px 9px 116px'
          : $isReply
            ? '20px 0px 0px 56px'
            : '33px 22px 0px 116px'};
  }

  @media (max-width: 499px) {
    ${({ $level }) => {
      if ($level > 2) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 1) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 500px) and (max-width: 569px) {
    ${({ $level }) => {
      if ($level > 3) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 2) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 570px) and (max-width: 639px) {
    ${({ $level }) => {
      if ($level > 4) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 3) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 640px) and (max-width: 709px) {
    ${({ $level }) => {
      if ($level > 5) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 4) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }}
  }

  @media (min-width: 710px) and (max-width: 767px) {
    ${({ $level }) => {
      if ($level > 6) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 5) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }};
  }

  @media (min-width: 768px) {
    ${({ $level }) => {
      if ($level > 4) {
        return css`
          padding-left: 0;

          .connector-svg {
            display: none;
          }
        `;
      }
      if ($level > 3) {
        return css`
          .connector-svg {
            display: none;
          }
        `;
      }
    }};
  }

  ${Divider} {
    display: ${({ $lastComment }) => ($lastComment ? 'none' : 'block')};
  }

  .markup {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    line-height: 120%;
    margin-bottom: 11px;

    p {
      margin: 0.75em 0 1em;
    }

    img {
      display: block;
      height: auto;
      max-width: 100%;
    }
  }
`;

const TopLine = styled.div`
  align-items: center;
  display: flex;
  gap: 11px;
  left: -31px;
  margin-bottom: 0.95rem;
  position: relative;

  h4 {
    font-family: 'Poppins', sans-serif;
    font-size: 0.85rem;
    font-weight: 700;

    span {
      color: var(--blue);
      font-weight: 400;
    }
  }

  @media (min-width: 768px) {
    gap: 10px;
    left: -56px;
    margin-bottom: 0.025rem;

    h4 {
      font-size: 1rem;
    }
  }
`;

const Timestamp = styled.span`
  bottom: 0.01em;
  display: inline-block;
  font-size: 0.75rem;
  position: relative;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const IsModerator = styled(Timestamp)`
  font-weight: 700 !important;
`;

const Buttons = styled.div`
  align-items: center;
  display: flex;

  button {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    white-space: nowrap;
  }
`;

export default Comment;
